import React from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArchiveRestore, Loader2, Trash2 } from 'lucide-react';
import { useTrashedTickets, useRestoreTicket, usePurgeTicket } from '../hooks/useTickets';
import { useHelpdeskNavigation } from '../hooks/useHelpdeskNavigation';
import { useDateFormat } from '../hooks/useDateFormat';
import { showToast } from '../lib/toast';
import { getErrorMessage } from '../lib/apiErrors';
import type { TrashedTicketResponse } from '../types/tickets';

interface DeletedTicketsSettingsProps {
  organizationId?: string;
}

/**
 * The organization's ticket trash (Org Settings → Deleted Tickets, admins). Each ticket can be restored or deleted
 * permanently before the scheduled purge removes it.
 */
export const DeletedTicketsSettings: React.FC<DeletedTicketsSettingsProps> = ({ organizationId }) => {
  const { data: tickets = [], isLoading, error } = useTrashedTickets(organizationId);
  const restoreMutation = useRestoreTicket();
  const purgeMutation = usePurgeTicket();
  const { getPath } = useHelpdeskNavigation();
  const { formatDateTime } = useDateFormat();

  const handleRestore = async (ticket: TrashedTicketResponse) => {
    try {
      await restoreMutation.mutateAsync(ticket.id);
      showToast.success('Ticket Restored', `Ticket #${ticket.ticketNumber} is back in the ticket list.`);
    } catch (err) {
      showToast.error('Restore Failed', getErrorMessage(err, 'Failed to restore ticket.'));
    }
  };

  const handlePurge = async (ticket: TrashedTicketResponse) => {
    try {
      await purgeMutation.mutateAsync(ticket.id);
      showToast.success('Ticket Deleted', `Ticket #${ticket.ticketNumber} was permanently deleted.`);
    } catch (err) {
      showToast.error('Delete Failed', getErrorMessage(err, 'Failed to delete ticket permanently.'));
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h3 className="text-base sm:text-lg font-semibold">Deleted Tickets</h3>
        <p className="text-xs sm:text-sm text-muted-foreground">
          Deleted tickets stay here for 30 days and can be restored. After that they're permanently deleted with their
          comments, history and attachments.
        </p>
      </div>

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
            Trash ({tickets.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading deleted tickets...
            </div>
          ) : error ? (
            <p className="text-sm text-destructive">{getErrorMessage(error, 'Failed to load deleted tickets.')}</p>
          ) : tickets.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">The trash is empty.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ticket</TableHead>
                    <TableHead className="hidden md:table-cell">Customer</TableHead>
                    <TableHead>Deleted</TableHead>
                    <TableHead className="hidden sm:table-cell">Purged on</TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.map((ticket) => (
                    <TableRow key={ticket.id}>
                      <TableCell>
                        <div className="min-w-0">
                          <p className="font-medium text-sm">#{ticket.ticketNumber}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[220px]" title={ticket.subject}>
                            {ticket.subject}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm">{ticket.customerName}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDateTime(ticket.deletedAt)}
                        {ticket.deletedByName && <span className="block">by {ticket.deletedByName}</span>}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-xs text-muted-foreground">
                        {formatDateTime(ticket.purgeAt)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => handleRestore(ticket)}
                            disabled={restoreMutation.isPending}
                            title="Restore"
                          >
                            <ArchiveRestore className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                disabled={purgeMutation.isPending}
                                title="Delete permanently"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Permanently delete ticket #{ticket.ticketNumber}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Its comments, history, attachments and participants are deleted now instead of on the
                                  scheduled purge. This can't be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handlePurge(ticket)}
                                  className="bg-destructive hover:bg-destructive/90"
                                >
                                  Delete Permanently
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-4">
            Restored tickets go back to the <Link to={getPath.tickets()} className="underline">ticket list</Link> with
            their status, assignee and history unchanged.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
